import { createPropertySelectors, createSelector } from '@ngxs/store';
import { denormalize } from 'normalizr';
import _ from 'lodash';

import { TicketItemsState, TicketItemsStateModel } from './ticket-items.state';
import {
  TicketItem,
  ticketItemsSchema,
  TicketItemStatus
} from 'src/app/models';
import { TicketsStateSelectors } from 'src/app/store/tickets/tickets.selectors';

export class TicketItemsStateSelectors {
  static slices =
    createPropertySelectors<TicketItemsStateModel>(TicketItemsState);

  static selectedTicketItemUuid = createSelector(
    [TicketItemsStateSelectors.slices.selectedTicketItem],
    (selectedTicketItem) => selectedTicketItem
  );

  private static ticketItems = createSelector(
    [TicketItemsStateSelectors.slices.ticketItems],
    (ticketItems) => ticketItems
  );

  static ticketItemsById = createSelector(
    [TicketItemsStateSelectors.ticketItems],
    (ticketItems) => ticketItems.byId
  );

  // static getSelectedTicketItemId = createSelector(
  //   [TicketItemsStateSelectors.selectedTicketItem],
  //   (selectedTicketItemId) => selectedTicketItemId
  // );

  static getTicketItems = createSelector(
    [TicketItemsStateSelectors.ticketItems],
    (ticketItems) => ticketItems
  );

  static getWaitingTicketItem = createSelector(
    [TicketItemsStateSelectors.slices.waitingTicketItem],
    (waitingTicketItem) => waitingTicketItem
  );

  static isSelectedTicketItem = createSelector(
    [TicketItemsStateSelectors.selectedTicketItemUuid],
    (selectedTiUuid) => (id: string) => id === selectedTiUuid
  );

  static getSelectedTicketItem = createSelector(
    [
      TicketItemsStateSelectors.selectedTicketItemUuid,
      TicketItemsStateSelectors.ticketItemsById
    ],
    (selectedTicketItem, ticket_items) => {
      if (!selectedTicketItem) {
        return null;
      }

      const denormalizedTicketItem: TicketItem = denormalize(
        selectedTicketItem,
        ticketItemsSchema,
        {
          ticket_items
        }
      );

      return denormalizedTicketItem;
    }
  );

  static havingItemAddingInProgress = createSelector(
    [TicketItemsStateSelectors.slices.havingItemAddingInProgress],
    (havingItemAddingInProgress) => havingItemAddingInProgress
  );

  static isTicketHavingItemAddingInProgress = createSelector(
    [
      TicketItemsStateSelectors.havingItemAddingInProgress,
      TicketsStateSelectors.selectedTicketId,
      TicketsStateSelectors.normalizedTicket
    ],
    (havingItemAddingInProgress, selectedTicketUuid, selectedTicket) => {
      if (!selectedTicketUuid || !selectedTicket) {
        return false;
      }
      const tableTicketKey = `${
        selectedTicket.table_id || 0
      }_${selectedTicketUuid}`;
      return (havingItemAddingInProgress?.[tableTicketKey] ?? 0) > 0;
    }
  );

  static isLoadingPrice = createSelector(
    [
      TicketItemsStateSelectors.getWaitingTicketItem,
      TicketItemsStateSelectors.isTicketHavingItemAddingInProgress,
      TicketsStateSelectors.selectedTicketId,
      TicketsStateSelectors.selectedProcessing
    ],
    (
      waitingTicketItems,
      isTicketHavingItemAddingInProgress,
      deviceTicketUuid,
      isTicketProcessing
    ) => {
      // Whole-ticket mutations (e.g. CancelTicket) flag selectedTicket.isProcessing.
      if (isTicketProcessing) {
        return true;
      }

      if (isTicketHavingItemAddingInProgress) {
        return true;
      }

      if (!waitingTicketItems?.length) {
        return false;
      }

      return waitingTicketItems[0].device_ticket_uuid === deviceTicketUuid;
    }
  );

  static isTableHavingItemAddingInProgress = createSelector(
    [TicketItemsStateSelectors.havingItemAddingInProgress],
    (havingItemAddingInProgress) => (tableId: number) => {
      if (!tableId) {
        return false;
      }
      const tablePrefix = `${tableId || 0}_`;
      return (
        Object.keys(havingItemAddingInProgress ?? {}).some((key) =>
          key.startsWith(tablePrefix)
        ) || false
      );
    }
  );

  static getTicketItemByUuid = createSelector(
    [TicketItemsStateSelectors.ticketItemsById],
    (ticketItemsById) => (ticketItemUuid: string) =>
      ticketItemsById && ticketItemsById[ticketItemUuid]
  );

  // move from self-service.selector.ts
  static getIsEmptyTicket = createSelector(
    [TicketItemsStateSelectors.ticketItemsById],
    (ticketItems) => _.every(ticketItems, (ti) => !ti.created_at)
  );

  static isSelectedTicketItemVoided = createSelector(
    [TicketItemsStateSelectors.getSelectedTicketItem],
    (selectedTicketItem) => {
      const selectedTicketItemStatus = selectedTicketItem?.status;
      if (!selectedTicketItemStatus) {
        return false;
      }

      return selectedTicketItemStatus === TicketItemStatus.VOIDED;
    }
  );
}
